import {
  createAICore,
  createLocalRulesProvider,
  rephraseWithCandidate,
  type AICandidate,
  type AICore,
  type AICoreResponse,
  type AIHistory,
  type AIProvider,
  type AIVisualizationCapability,
} from '@aibi/ai-core';
import { createAnalysisState, planAnalysis } from '@aibi/analysis-engine';
import type { AnalysisState, ScalarValue, VisualizationType } from '@aibi/contracts';
import { SemanticModelIndex } from '@aibi/semantic-model';
import { AppError, err, ok, type Result } from '@aibi/shared';
import type { VisualizationDescriptor } from '@aibi/visualization-engine';
import type {
  AssistantCandidate,
  AssistantPort,
  AssistantReply,
  AssistantRequest,
} from './assistant-port';
import type { SemanticModelCatalog } from './semantic-model-catalog';

/** Id de la tarjeta candidata cuando no hay ninguna seleccionada (se reasigna al aplicar). */
export const NEW_CARD_DRAFT_ID = 'card-ai-draft';

export interface AICoreAssistantOptions {
  readonly modelCatalog: SemanticModelCatalog;
  /** Visualizaciones registradas (integradas y de plugins). */
  readonly visualizations: () => readonly VisualizationDescriptor[];
  /** Solo se ofrecen al asistente las visualizaciones que la UI sabe dibujar. */
  readonly isRenderable: (type: VisualizationType) => boolean;
  /** Miembros conocidos por dimension (para resolver filtros); nunca muestras crudas. */
  readonly members?: Readonly<Record<string, readonly ScalarValue[]>>;
  /** Proveedor de IA; por defecto, el interprete local de reglas (sin red). */
  readonly provider?: AIProvider;
}

const finite = (value: number): number | null => (Number.isFinite(value) ? value : null);

/** Capacidades de visualizacion como datos para el AI Core (`Infinity` -> `null`). */
export function toVisualizationCapabilities(
  descriptors: readonly VisualizationDescriptor[],
  isRenderable: (type: VisualizationType) => boolean,
): AIVisualizationCapability[] {
  return descriptors
    .filter((descriptor) => isRenderable(descriptor.id))
    .map(({ id, label, requirements }) => ({
      type: id,
      label,
      minDimensions: requirements.minDimensions,
      maxDimensions: finite(requirements.maxDimensions),
      minMeasures: requirements.minMeasures,
      maxMeasures: finite(requirements.maxMeasures),
      requiresTemporal: requirements.requiresTemporal === true,
      requiresGeographic: requirements.requiresGeographic === true,
    }));
}

/**
 * Asistente de la web sobre el AI Core en proceso. Traduce la respuesta del AI Core al
 * `AssistantReply` de la UI; nunca modifica el workspace (las propuestas se aplican con
 * el comando `apply-ai-proposal` tras confirmacion explicita del usuario).
 *
 * @example
 * const assistant = createAICoreAssistant({ modelCatalog, visualizations: () => registry.list(), isRenderable });
 * const reply = await assistant.ask({ prompt: 'ventas por region', context });
 */
export function createAICoreAssistant(
  options: AICoreAssistantOptions,
): AssistantPort & { readonly history: AIHistory } {
  const core: AICore = createAICore({ provider: options.provider ?? createLocalRulesProvider() });
  const indexes = new Map<string, SemanticModelIndex>();

  const loadIndex = async (modelId: string): Promise<Result<SemanticModelIndex, AppError>> => {
    const cached = indexes.get(modelId);
    if (cached) return ok(cached);
    const loaded = await options.modelCatalog.load(modelId);
    if (!loaded.ok) return loaded;
    const index = new SemanticModelIndex(loaded.value);
    indexes.set(modelId, index);
    return ok(index);
  };

  return {
    available: true,
    providerName: core.provider.displayName,
    connection: core.provider.kind === 'local' ? 'local' : 'unverified',
    history: core.history,
    async ask(request: AssistantRequest, signal?: AbortSignal) {
      const { semanticModelId, analysisState } = request.context;
      const index = await loadIndex(semanticModelId);
      if (!index.ok) return index;
      const base: AnalysisState =
        analysisState ?? createAnalysisState({ id: NEW_CARD_DRAFT_ID, semanticModelId });
      const planned = planAnalysis(base, index.value.model);
      const response = await core.handle(
        {
          prompt: request.prompt,
          index: index.value,
          state: base,
          visualizations: toVisualizationCapabilities(
            options.visualizations(),
            options.isRenderable,
          ),
          ...(options.members === undefined ? {} : { members: options.members }),
          plan: planned.ok ? planned.value.plan : null,
        },
        signal,
      );
      if (response.kind === 'error' && response.error.code === 'CANCELLED') {
        return err(new AppError('CANCELLED', 'Solicitud cancelada.'));
      }
      return ok(toReply(response, request.prompt, analysisState));
    },
  };
}

const candidateOf = (candidate: AICandidate, followUp: string | null): AssistantCandidate => ({
  id: candidate.id,
  label: candidate.label,
  kind: candidate.kind,
  ...(candidate.dimensionId === undefined ? {} : { dimensionId: candidate.dimensionId }),
  followUp,
});

function toReply(
  response: AICoreResponse,
  prompt: string,
  analysisState: AnalysisState | null,
): AssistantReply {
  const { requestId } = response;
  switch (response.kind) {
    case 'proposal':
      return {
        kind: 'proposal',
        requestId,
        summary: response.summary,
        ...(response.rationale === undefined ? {} : { rationale: response.rationale }),
        baseState: analysisState,
        state: response.stateActionIds.length > 0 ? response.state : null,
        modelChanges: response.modelChanges.map((change) => ({
          actionId: change.actionId,
          summary: `${change.actionType === 'createMeasure' ? 'Crear medida' : 'Crear dimension'} "${change.objectLabel}"`,
        })),
      };
    case 'clarification': {
      const { clarification } = response;
      return {
        kind: 'clarification',
        requestId,
        question: clarification.question,
        reason: clarification.reason,
        candidates: clarification.candidates.map((candidate) =>
          candidateOf(candidate, rephraseWithCandidate(prompt, clarification, candidate)),
        ),
      };
    }
    case 'explanation':
      return {
        kind: 'explanation',
        requestId,
        title: response.explanation.title,
        summary: response.explanation.summary,
        facts: response.explanation.facts,
      };
    case 'suggestion':
      return {
        kind: 'suggestion',
        requestId,
        cardId: analysisState?.id ?? null,
        visualization: response.suggestion.type,
        label: response.suggestion.label,
        rationale: response.suggestion.rationale,
      };
    case 'error':
      return {
        kind: 'error',
        requestId,
        code: response.error.code,
        message: response.error.message,
        candidates: (response.error.candidates ?? []).map((candidate) =>
          candidateOf(candidate, null),
        ),
      };
  }
}
